import type React from 'react';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import SkeletonLoader from '../../../skeletonLoader/SkeletonLoader';
import type { MeasuringPointWithProject, ProjectOverviewRow } from '../types';
import {
    getMeasuringPointColumnHeaders,
    MeasuringPointHeaderCell,
    MEASURING_POINT_HEADER_ROW_CLASSES,
    MeasuringPointsEmptyMessage,
    MeasuringPointsLoadFailedMessage,
} from './ProjectMeasuringPointList';
import { ProjectMeasuringPointRow } from './ProjectMeasuringPointRow/ProjectMeasuringPointRow';
import {
    getMeasuringPointRowKey,
    mapOverviewQueryMeasuringPoints,
    uniqueMeasuringPoints,
} from '../utils/measuringPoints';
import { OVERVIEW_GRID_COLUMN_COUNT } from '../table/overviewGrid';
import { getExpandedMeasuringPointsQuery } from '../hooks/useLoadExpandedMeasuringPoints';

function MeasuringPointHeaderRow({ id }: { id: string }): React.ReactElement {
    return (
        <tr id={id} className={MEASURING_POINT_HEADER_ROW_CLASSES}>
            <td />
            {getMeasuringPointColumnHeaders(true).map((header) => (
                <MeasuringPointHeaderCell key={header.key} header={header} />
            ))}
            <td />
        </tr>
    );
}

function FullWidthRow({
    id,
    children,
}: {
    id: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <tr id={id}>
            <td colSpan={OVERVIEW_GRID_COLUMN_COUNT} className="p-4">
                {children}
            </td>
        </tr>
    );
}

interface ExpandedProjectMeasuringPointsProps {
    projectRow: ProjectOverviewRow;
    archived: boolean;
    /** Target of the project row's expand button (`aria-controls`). */
    detailsId: string;
    loadFailed: boolean;
}

/**
 * The measuring point rows below an expanded project row, on the grid from `overviewGrid.ts`.
 */
export function ExpandedProjectMeasuringPoints({
    projectRow,
    archived,
    detailsId,
    loadFailed,
}: ExpandedProjectMeasuringPointsProps): React.ReactElement {
    const { projectId, projectName, measuringPointCount } = projectRow;
    const { query, variables } = getExpandedMeasuringPointsQuery(projectId, archived);
    // The rows read what `useLoadExpandedMeasuringPoints` fetched for all expanded projects.
    const { data, error } = useQuery(query, {
        variables,
        fetchPolicy: 'cache-only',
        skip: measuringPointCount === 0,
    });

    const measuringPoints = useMemo<MeasuringPointWithProject[]>(
        () =>
            uniqueMeasuringPoints(
                mapOverviewQueryMeasuringPoints(data?.measuringPoints, { projectId, projectName })
            ),
        [data?.measuringPoints, projectId, projectName]
    );

    if (error) {
        throw error;
    }

    if (loadFailed) {
        return (
            <FullWidthRow id={detailsId}>
                <MeasuringPointsLoadFailedMessage />
            </FullWidthRow>
        );
    }

    if (measuringPointCount > 0 && !data) {
        return (
            <FullWidthRow id={detailsId}>
                <SkeletonLoader count={3} className="h-4" />
            </FullWidthRow>
        );
    }

    if (measuringPoints.length === 0) {
        return (
            <FullWidthRow id={detailsId}>
                <MeasuringPointsEmptyMessage project={projectRow} />
            </FullWidthRow>
        );
    }

    return (
        <>
            <MeasuringPointHeaderRow id={detailsId} />
            {measuringPoints.map((measuringPoint) => (
                <ProjectMeasuringPointRow
                    key={getMeasuringPointRowKey(measuringPoint)}
                    measuringPoint={measuringPoint}
                    layout="projectGrid"
                    isProjectArchived={projectRow.archived}
                />
            ))}
        </>
    );
}
